import { AppTranscriptCardStyles } from './AppTranscriptCard.props';
import { FontWeights } from '@uifabric/styling';
import { mergeStyles, mergeStyleSets, IProcessedStyleSet } from 'office-ui-fabric-react';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { ILabelStyles, ILinkStyles, IButtonStyles, ITextFieldStyles, ICheckboxStyles, ITooltipHostStyles, concatStyleSets } from 'office-ui-fabric-react';
import { HostAppSettingName, HostAppSettings } from '../../../backend/office-js/HostAppSettings';

export const appTranscriptCardIconButtonClassName = 'appTranscriptCardIconButtonClass';

export const getAppTranscriptCardStyles = (
  isHighlighted?: boolean,
  customStyles?: Partial<AppTranscriptCardStyles>
): IProcessedStyleSet<AppTranscriptCardStyles> => {
  const styles: AppTranscriptCardStyles = {
    root: [
      {
        width: '100%',
        padding: '10px 16px 10px 16px',
        boxSizing: 'border-box',
        selectors: {
          [`:hover, :focus-within`]: {
            background: AppLayoutSettings.getAppColorContainer().lightBackgroundColor.value,
            borderRadius: '2px',
            boxShadow: '0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13), 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11)'
          },
          [`:hover .${appTranscriptCardIconButtonClassName}, :focus-within .${appTranscriptCardIconButtonClassName}`]: {
            visibility: 'visible'
          },
          ['@media screen and (-ms-high-contrast: active)']: {
            border: isHighlighted ? 'solid 1px' : undefined,
            borderColor: isHighlighted ? 'highlight' : undefined
          }
        }
      },
      isHighlighted ? { background: AppLayoutSettings.getAppColorContainer().lightBackgroundColor.value } : { background: 'transparent' }
    ],
    topRowContainer: {
      display: 'flex'
    },
    bottomRowContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: '14px'
    }
  };

  return mergeStyleSets(customStyles, styles);
};

export const getAppTranscriptCardIconButtonStyles = (icon: string, forceVisible?: boolean): Partial<IButtonStyles> => {
  const defaultStyles: Partial<IButtonStyles> = {
    root: {
      minWidth: '16px',
      minHeight: '16px',
      background: 'transparent'
    },
    rootHovered: {
      background: 'transparent'
    }
  };

  const visibleValue = forceVisible ? 'visible' : 'hidden';
  let customStyles: Partial<IButtonStyles> = {};

  switch (icon) {
    case 'edit':
      customStyles = {
        root: {
          visibility: visibleValue,
          height: '14px',
          border: 'none',
        },
        icon: {
          fontSize: AppLayoutSettings.AppFontSize.largeFontSizePx,
          color: AppLayoutSettings.getAppColorContainer().mediumLabelColor.value
        }
      };
      break;
    case 'add':
      customStyles = {
        root: {
          visibility: visibleValue,
          height: '16px',
          border: 'none'
        },
        icon: {
          marginRight: '0px',
          fontSize: AppLayoutSettings.AppFontSize.largePlusFontSizePx,
          color: AppLayoutSettings.getAppColorContainer().mediumLabelColor.value
        }
      };
      break;
    case 'confirm':
    case 'cancel':
      customStyles = {
        root: {
          height: '14px',
          minHeight: '14px',
          padding: '0px',
          border: 'none'
        },
        icon: {
          padding: '0px 0px 0px 4px',
          fontSize: AppLayoutSettings.AppFontSize.largeFontSizePx,
          color: AppLayoutSettings.getAppColorContainer().mediumLabelColor.value
        }
      };
      break;
  }

  return mergeStyleSets(defaultStyles, customStyles);
};

const getAppTranscriptCardTimestampStyles = (): ILabelStyles => {
  return {
    root: {
      padding: '4px 0px 0px 0px'
    }
  };
}

export const getAppTranscriptCardTimestampLinkStyles = (isDisabled: boolean = false): ILinkStyles => {
  const commonLinkStyles = AppLayoutSettings.AppCommonStyles.getLinkStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, isDisabled ?  AppLayoutSettings.getAppColorContainer().lightLabelColor : AppLayoutSettings.getAppColorContainer().linkColor);
  const specificStyle = getAppTranscriptCardTimestampStyles();

  return concatStyleSets(commonLinkStyles, specificStyle);
};

export const getAppTranscriptCardTimestampLabelStyles = (isDisabled: boolean = false): ILabelStyles => {
  const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getBoldLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx,  isDisabled ?  AppLayoutSettings.getAppColorContainer().lightLabelColor : AppLayoutSettings.getAppColorContainer().timestampLabelColor);
  const specificStyle = getAppTranscriptCardTimestampStyles();

  return concatStyleSets(commonLabelStyles, specificStyle);
};

export const getAppTranscriptCardTranscriptLabelStyles = (isDisabled: boolean = false): ILabelStyles => {
  const commonLabelStyles = AppLayoutSettings.AppCommonStyles.getRegularLabelStyles(AppLayoutSettings.AppFontSize.mediumFontSizePx, (isDisabled ? AppLayoutSettings.getAppColorContainer().lightLabelColor : AppLayoutSettings.getAppColorContainer().mediumLabelColor));
  const specificStyle: ILabelStyles = {
    root: {
      padding: '0px 0px 4px 0px',
    }
  };

  return {
    root: mergeStyles(commonLabelStyles.root, specificStyle.root)
  };
};

export const getAppTranscriptCardSpeakerLabelStyles = (isDisabled:boolean = false): ILabelStyles => {
  return {
    root: [
      {
        color: isDisabled ? AppLayoutSettings.getAppColorContainer().lightLabelColor.value : AppLayoutSettings.getAppColorContainer().darkLabelColor.value,
        fontSize: AppLayoutSettings.AppFontSize.mediumFontSizePx,
        fontWeight: FontWeights.semibold,
        margin: '3px 0 0 10px',
        padding: '0px 0px 4px 0px',
      }
    ]
  };
};

export const getAppTranscriptCardTextFieldStyles = (textField: string): Partial<ITextFieldStyles> => {
  let textFieldStyles: Partial<ITextFieldStyles> = {};
  switch (textField) {
    case 'transcript':
      textFieldStyles = {
        root: {
          margin: '10px 0 0 0'
        },
        field: {
          fontSize: AppLayoutSettings.AppFontSize.mediumFontSizePx,
          padding: '8px',
          color: AppLayoutSettings.getAppColorContainer().darkLabelColor.value,
          backgroundColor: AppLayoutSettings.getAppColorContainer().whiteColor.value
        },
        fieldGroup: {
          border: '1px solid #2b579a'
        }
      };
      break;
    case 'speaker':
      textFieldStyles = {
        root: [
          {
            width: '100%'
          }
        ],
        field: {
          fontSize: AppLayoutSettings.AppFontSize.mediumFontSizePx,
          color: AppLayoutSettings.getAppColorContainer().darkLabelColor.value,
          backgroundColor: AppLayoutSettings.getAppColorContainer().whiteColor.value
        },
        fieldGroup: {
          height: '20px',
          margin: '0 10px',
          border: '1px solid #E5E5E5',
        }
      };
      break;
  }

  return textFieldStyles;
};

export const getAppTranscriptCardCheckboxStyles = (): Partial<ICheckboxStyles> => {
  return {
    root: [
      {
        margin: '5px 0 0 35px',
        selectors: {
          [" :hover"]: {
            selectors: {
              [" .ms-Checkbox-text"]: {
                color: HostAppSettings.IsSettingTrue(HostAppSettingName.IsSharedUXDarkModeOn) ? `${AppLayoutSettings.getAppColorContainer().blackColor.value} !important` : 'windowText !important',
              },
            },
          },
        },
      }
    ],
    checkbox: {
      marginTop: '4px',
      height: '12px',
      width: '12px',
      //Note: !important is used to disable the hover state color change of checkbox. Otherwise, the checkbox border color is getting dark and not visible in dark mode.
      borderColor:  HostAppSettings.IsSettingTrue(HostAppSettingName.IsSharedUXDarkModeOn) ? `${AppLayoutSettings.getAppColorContainer().darkLabelColor.value} !important` : 'windowText !important',
    },
    text: {
      fontSize: AppLayoutSettings.AppFontSize.smallPlusFontSizePx,
      color: `${AppLayoutSettings.getAppColorContainer().darkLabelColor.value}`,
      selectors: {
        [`:hover, :focus-within`]: {
          // If dark contrast theme is enabled, we don't get 'IsSharedUXDarkModeOn' setting as true, so we will rely on windowText property in that case. Else
          // if 'IsSharedUXDarkModeOn' is true i.e. dark mode is enabled through app, then we will use 'blackColor' value from ColorContainer.
          color: HostAppSettings.IsSettingTrue(HostAppSettingName.IsSharedUXDarkModeOn) ? `${AppLayoutSettings.getAppColorContainer().blackColor.value} !important` : 'windowText !important',
        },
      }
    }
  }
};

export const getAddToDocumentTooltipHostStyles = (): ITooltipHostStyles => {
  const tooltipHostStyles: ITooltipHostStyles = {
    root: {
      position: 'absolute',
      right: '0px'
    }
  };

  return tooltipHostStyles;
};

export const getAppTranscriptCardScrollStyles = (): ScrollIntoViewOptions => {
  const scrollIntoViewOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  };

  return scrollIntoViewOptions;
};